import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getArtistApplication,
  approveArtistApplication,
  rejectArtistApplication,
} from '../../services/adminService';

const STATUS_CONFIG = {
  pending:  { label: 'Pendiente',  color: '#f0a500', bg: '#fef9e7' },
  approved: { label: 'Aprobada',   color: '#28a745', bg: '#eaf7ed' },
  rejected: { label: 'Rechazada',  color: '#dc3545', bg: '#fde8ea' },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#666', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '0.85rem',
      fontWeight: '600',
      color: cfg.color,
      background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
};

const Field = ({ label, value, link }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '1rem' }}>
      <p style={{ margin: '0 0 0.25rem', fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
        {label}
      </p>
      {link
        ? <a href={value} target="_blank" rel="noreferrer" style={{ color: '#2ca9e1', wordBreak: 'break-all' }}>{value}</a>
        : <p style={{ margin: 0, color: '#333', whiteSpace: 'pre-wrap' }}>{value}</p>}
    </div>
  );
};

const ImageThumb = ({ src, alt }) => {
  if (!src) return null;
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img
        src={src}
        alt={alt}
        style={{
          width: 120,
          height: 120,
          objectFit: 'cover',
          borderRadius: 8,
          border: '1px solid #eee',
        }}
      />
    </a>
  );
};

const AdminArtistApplicationDetail = () => {
  const { id } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    getArtistApplication(session.access_token, id)
      .then((data) => {
        if (cancelled) return;
        setApplication(data.application);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [session?.access_token, id]);

  const handleApprove = async () => {
    if (!window.confirm(`¿Aprobar la solicitud de ${application.name}?`)) return;
    setBusy(true);
    try {
      const { application: updated } = await approveArtistApplication(session.access_token, id);
      setApplication(updated);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      alert('Debes incluir un motivo de rechazo.');
      return;
    }
    if (reason.length > 500) {
      alert('El motivo no puede superar los 500 caracteres.');
      return;
    }
    setBusy(true);
    try {
      const { application: updated } = await rejectArtistApplication(session.access_token, id, reason);
      setApplication(updated);
      setRejectMode(false);
      setRejectReason('');
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="container" style={{ padding: '4rem 0' }}>Cargando...</div>;
  }
  if (error) {
    return (
      <div className="container" style={{ padding: '4rem 0' }}>
        <p style={{ color: '#dc3545' }}>{error}</p>
        <button onClick={() => navigate('/admin/artist-applications')} style={{ marginTop: '1rem' }}>
          ← Volver
        </button>
      </div>
    );
  }
  if (!application) return null;

  const isPending = application.status === 'pending';

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      <Link to="/admin/artist-applications" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>
        ← Solicitudes
      </Link>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', margin: '0.5rem 0 2rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.75rem', color: '#333', margin: 0 }}>
            {application.name}
          </h1>
          {application.creatorName && (
            <p style={{ margin: '0.2rem 0 0', color: '#666', fontSize: '0.95rem', fontStyle: 'italic' }}>
              por {application.creatorName}
            </p>
          )}
        </div>
        {application.slug && <span style={{ color: '#888' }}>@{application.slug}</span>}
        <StatusBadge status={application.status} />
      </div>

      {/* Action bar */}
      <div style={{ background: 'white', padding: '1.25rem 1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1.5rem' }}>
        {isPending ? (
          rejectMode ? (
            <div>
              <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Motivo del rechazo</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explica al solicitante por qué su aplicación fue rechazada (visible para él)."
                rows={4}
                maxLength={500}
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <p style={{ color: '#888', fontSize: '0.78rem', margin: '0.25rem 0 0.75rem' }}>
                {rejectReason.length}/500
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={busy || !rejectReason.trim()}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: busy || !rejectReason.trim() ? 'not-allowed' : 'pointer',
                    opacity: busy || !rejectReason.trim() ? 0.6 : 1,
                  }}
                >
                  {busy ? 'Rechazando...' : 'Confirmar rechazo'}
                </button>
                <button
                  type="button"
                  onClick={() => { setRejectMode(false); setRejectReason(''); }}
                  disabled={busy}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: 'white',
                    color: '#666',
                    border: '1.5px solid #ddd',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleApprove}
                disabled={busy}
                style={{
                  padding: '0.6rem 1.25rem',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Procesando...' : 'Aprobar'}
              </button>
              <button
                type="button"
                onClick={() => setRejectMode(true)}
                disabled={busy}
                style={{
                  padding: '0.6rem 1.25rem',
                  background: 'white',
                  color: '#dc3545',
                  border: '1.5px solid #dc3545',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                Rechazar
              </button>
            </div>
          )
        ) : (
          <div style={{ color: '#555', fontSize: '0.9rem' }}>
            Revisada por <strong>{application.reviewedByEmail || '—'}</strong>
            {application.reviewedAt && <> el {formatDate(application.reviewedAt)}</>}
            {application.status === 'rejected' && application.rejectReason && (
              <p style={{ margin: '0.5rem 0 0', color: '#dc3545' }}>
                Motivo: {application.rejectReason}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Details + images */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '1.5rem' }}>
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Field label="Solicitante" value={application.customerEmail} />
          <Field label="Enviada" value={formatDate(application.createdAt)} />
          <Field label="Nacionalidad" value={application.nationality} />
          <Field label={application.creatorName ? "Acerca de la marca/proyecto" : "Acerca de"} value={application.about} />
          {application.creatorName && (
            <Field label={`Acerca de ${application.creatorName}`} value={application.creatorAbout} />
          )}
          <Field label="Manifiesto" value={application.manifesto} />
          <Field label="Derechos reservados" value={application.rightsReserved} />
          <Field label="Color de acento" value={application.accentColor} />
          <Field label="Sitio web" value={application.websiteUrl} link />
          <Field label="Instagram" value={application.instagramHandle ? `@${application.instagramHandle}` : null} />
          {/* Bank details (migration 090) — copy them into the artist's profile, with the consent checkbox, when creating it. */}
          <Field label="Banco" value={application.bankName} />
          <Field label="Titular de la cuenta" value={application.bankBeneficiary} />
          <Field label="CLABE" value={application.bankClabe} />
          <Field
            label="Autorización de datos bancarios"
            value={application.bankConsentAt ? `Aceptada el ${new Date(application.bankConsentAt).toLocaleDateString('es-MX')}` : null}
          />
        </div>

        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
            Imágenes
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <ImageThumb src={application.profileImageUrl} alt="Perfil" />
            <ImageThumb src={application.coverImageUrl} alt="Portada" />
            <ImageThumb src={application.showcaseImage1Url} alt="Showcase 1" />
            <ImageThumb src={application.showcaseImage2Url} alt="Showcase 2" />
            <ImageThumb src={application.showcaseImage3Url} alt="Showcase 3" />
            {application.processImages?.map((url, i) => (
              <ImageThumb key={i} src={url} alt={`Proceso ${i + 1}`} />
            ))}
            <ImageThumb src={application.signatureSvgUrl} alt="Firma" />
            <ImageThumb src={application.creatorProfileImageUrl} alt="Foto del creador" />
          </div>
          {application.studioVideoUrl && (
            <p style={{ margin: '1rem 0 0' }}>
              <a href={application.studioVideoUrl} target="_blank" rel="noreferrer" style={{ color: '#2ca9e1' }}>
                Ver video del estudio →
              </a>
            </p>
          )}
          {application.studioVideoCaption && (
            <p style={{ margin: '0.5rem 0 0', color: '#555', fontSize: '0.92rem' }}>
              <strong style={{ color: '#888', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '0.2rem' }}>
                Texto de la sección "En el estudio"
              </strong>
              {application.studioVideoCaption}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminArtistApplicationDetail;
